import {
    Flex,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    useBreakpointValue,
    Button,
    Textarea,
    FormControl,
    FormLabel,
    Text,
} from "@chakra-ui/react";
import { AppContext } from "AppProvider/AppContext";
import { appRequest } from "routes";
import { useContext, useState, useMemo, useEffect } from "react";
import SrProcessTable from "./SrProcessTable";
import FileViewer from "components/common/FileViewerPdfAndImage/FileViewer";
import { Box } from "@chakra-ui/react";

export default function SrProcess({
    file,
    isOpen,
    onClose,
    item,
    handleGet,
    showApproval,
    name,
    showModalDrawer
}: any): JSX.Element {
    const { toastNotify, workflowProcess, workFlowData, accountDetails } = useContext(AppContext);
    const viewerWidth = useBreakpointValue({ lg: true });
    const width = file ? "97vw" : "95vw";

    /* ================= FORM DATA ================= */
    const [decisionDrawerOpen, setDecisionDrawerOpen] = useState(false);
    const [decisionType, setDecisionType] = useState<"approved" | "rejected">(
        "approved",
    );
    const [comment, setComment] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [sample, setSample] = useState({
        url: "",
        file: null,
        type: null,
        buffer: null,
    });

    /* ================= GET FILE ================= */
    useEffect(() => {
        const fetchFile = async () => {
            if (file && file._id) {
                try {
                    const response: any = await appRequest("filesystem", "get", {
                        id: file._id,
                    });
                    if (response && !response.error) {
                        let buffer = typeof response === "string" ? JSON.parse(response) : response;
                        buffer = new Uint8Array(buffer);
                        const blob = new Blob([buffer], { type: file.metadata?.contentType || "application/pdf" });
                        const url = URL.createObjectURL(blob);
                        setSample({
                            url: url,
                            file: file,
                            type: file.metadata?.contentType,
                            buffer: buffer,
                        });
                    }
                } catch (error) {
                    console.error("Error fetching file:", error);
                }
            } else if (typeof file === "string") {
                setSample((prev) => ({ ...prev, url: file }));
            }
        };

        fetchFile();

        return () => {
            if (sample.url && sample.url.startsWith("blob:")) {
                URL.revokeObjectURL(sample.url);
            }
        };
    }, [file]);




    const lineItems = useMemo(() => {
        if (!item?.values || !Array.isArray(item.values) || item.values.length === 0) {
            return [];
        }
        const latest = item.values[item.values.length - 1];
        return latest?.values?.tables?.SR_Line_Items || [];
    }, [item]);

    const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        const chunkSize = 0x8000;

        for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }

        return btoa(binary);
    };

    /* ================= SUBMIT DECISION ================= */
    const submitDecision = async (decision: "yes" | "no") => {
        try {
            setLoading(true);

            const updatedStatus = {
                id: item?._id,
                level: accountDetails?.level,
                status: decisionType,
                comment: comment,
                values: {
                    tables: {
                        SR_Line_Items: lineItems,
                    },
                },
                activeWorkflow: workFlowData?._id,
            };

            const response: any = await appRequest(
                "document",
                "update",
                updatedStatus,
            );

            if (response?.status === "success") {
                if (response?.message === "SES Deleted Successfully") {
                          const response2: any = await appRequest(
                            "document",
                            "rejectPrSrDocument",
                            {...updatedStatus, documentId : item?._id, comment : response?.message},
                          );
                        }
                toastNotify({
                    title: "Success",
                    message: response?.message || "Decision submitted successfully",
                    type: "success",
                });

                if (onClose) {
                    onClose();
                    handleGet()
                }
            } else {
                throw new Error(response?.message || "Failed to update document");
            }

            setDecisionDrawerOpen(false);
            setComment("");
            setPassword("");
        } catch (err: any) {
            toastNotify({
                title: "Error",
                message: err?.message || "Failed to submit decision",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
                <DrawerOverlay />
                <DrawerContent maxW={viewerWidth ? width : "100vw"}>
                    <DrawerHeader borderBottomWidth="1px" pb={4} bg="white">
                        <Flex align="center" justify="space-between" width="100%">
                            <Flex direction="column" gap={1}>
                                <Text fontSize="lg" fontWeight="bold" color="teal.600">
                                    Service Requisition (SR) Details
                                </Text>
                                {(item?.originalValues?.SR_Number || item?.originalValues?.SR_Id) && (
                                    <Text fontSize="sm" color="gray.500" fontWeight="normal">
                                        SR Number: {item?.originalValues?.SR_Number || item?.originalValues?.SR_Id}
                                    </Text>
                                )}
                            </Flex>
                        </Flex>
                    </DrawerHeader>
                    <DrawerCloseButton mt={1} />

                    <DrawerBody height="95vh" p={0} bg="gray.100">
                        <Flex display={showModalDrawer === true ? "none" : undefined} height="100%" width="100%" direction={{ base: "column", lg: "row" }}>
                            {/* PDF Viewer Section */}
                            <Box
                                flex={{ base: "1", lg: "0.4" }}
                                height={{ base: "50%", lg: "100%" }}
                                borderRightWidth="1px"
                                borderBottomWidth={{ base: "1px", lg: "0" }}
                                bg="white"
                                overflow="hidden"
                            >
                                <FileViewer sample={sample} docId={item?.documentId} />
                            </Box>

                            {/* Table Section */}
                            <Box
                                flex={{ base: "1", lg: "0.6" }}
                                height={{ base: "50%", lg: "100%" }}
                                p={{ base: 4, lg: 6 }}
                                overflowY="auto"
                            >
                                <SrProcessTable data={lineItems} />
                            </Box>
                        </Flex>
                    </DrawerBody>
                              <DrawerFooter display={(name !== "Pending Approval" || accountDetails?.level === "admin") ? "none" : undefined}>
                        <Flex gap={3}>
                            <Button
                                colorScheme="green"
                                onClick={() => {
                                    setDecisionType("approved");
                                    setDecisionDrawerOpen(true);
                                }}
                            >
                                Accepted
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={() => {
                                    setDecisionType("rejected");
                                    setDecisionDrawerOpen(true);
                                }}
                            >
                                Rejected
                            </Button>
                        </Flex>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            {/* Decision Comment Drawer */}
            <Drawer
                isOpen={decisionDrawerOpen}
                placement="bottom"
                onClose={() => setDecisionDrawerOpen(false)}
                size="md"
            >
                <DrawerOverlay />
                <DrawerContent borderTopRadius="xl">
                    <DrawerHeader>
                        {decisionType === "approved"
                            ? "Approval Confirmation"
                            : "Rejection Confirmation"}
                        <DrawerCloseButton />
                    </DrawerHeader>

                    <DrawerBody>
                        <FormControl mb={4}>
                            <FormLabel>Comment</FormLabel>
                            <Textarea
                                placeholder="Enter your comment"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                        </FormControl>
                    </DrawerBody>

                    <DrawerFooter>
                        <Flex w="100%" justify="flex-end" gap={3}>
                            <Button
                                colorScheme="red"
                                onClick={() => setDecisionDrawerOpen(false)}
                            >
                                No
                            </Button>
                            <Button
                                colorScheme="green"
                                isLoading={loading}
                                onClick={() => submitDecision("yes")}
                            >
                                Yes
                            </Button>
                        </Flex>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
        </>
    );
}